"use client";

import React from "react";
import Image from "next/image";
import { ArticleAuthors } from "../../partials/ArticleAuthors/ArticleAuthors";
import { ArticleBody } from "../../partials/ArticleBody/ArticleBody";
import { TabbedRelatedContent } from "../../partials/TabbedArticles/TabbedArticles";
import { RelatedMatches } from "../../partials/RelatedMatches/RelatedMatches";
import { ArticleMatchDetail } from "../../partials/ArticleMatchDetail/ArticleMatchDetail";
import { ArticlePublishedInfo } from "../../partials/shared/ArticlePublishedInfo";
import { useArticleData } from "../../context/ArticleDataContext";
import type { LocaleCode } from "@/lib/labels/types";
import { AdSlot } from "@/features/ads/AdSlot";
import { DiscussionWidget } from "@/features/discussion/DiscussionWidget";
import type { Media } from "@/payload-types";
import { ChallengeZoneSidebar } from "@/features/challenge-zone/ChallengeZoneSidebar";
import { getMediaUrl } from "@/utilities/media/getMediaUrl";
import { MediaCredit } from "@/components/MediaCredit/MediaCredit";
import { MediaCaption } from "@/components/MediaCaption/MediaCaption";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { ARTICLE_HERO, ARTICLE_RELATED } from "@/features/theme/sectionKeys";
import { useAuth } from "@/lib/auth/AuthContext";

/**
 * Standard article layout variant.
 * Layout structure:
 * - Title at top
 * - Hero image below title
 * - Author and metadata (date, reading time)
 * - Article body content
 * - Right sidebar with banner placeholder + tabbed content
 */
interface ArticleLayoutStandardProps {
    sectionStyles?: Record<string, BrandedStyle>;
    breadcrumbs?: React.ReactNode;
}

export function ArticleLayoutStandard({ sectionStyles, breadcrumbs }: ArticleLayoutStandardProps) {
    const { post, locale: localeStr, primaryCategory, relatedEntities } = useArticleData();
    const locale = localeStr as LocaleCode;
    const { isAuthEnabled } = useAuth();

    const heroImage = post.heroImage as Media | null;
    const heroImageUrl = getMediaUrl(heroImage, "xlarge");

    // Get category name for display
    const categoryName = primaryCategory?.title;

    return (
        <article className="min-h-screen">
            {/* Main Container */}
            <div className="container mx-auto px-4 py-8 md:py-12 space-y-8">
                <AdSlot id="content-top" />

                <div className="flex flex-col md:flex-row gap-8">
                    {/* Main Content */}
                    <div className="flex-1 min-w-0 max-w-240">
                        {/* Hero Section */}
                        <SectionStrip brandedStyle={sectionStyles?.[ARTICLE_HERO]}>
                            {breadcrumbs ? <div className="mb-6">{breadcrumbs}</div> : null}
                            {/* Category Badge */}
                            {categoryName && (
                                <div className="mb-4">
                                    <span className="inline-block px-3 py-1 text-xs font-medium uppercase tracking-wide bg-primary text-primary-foreground rounded">
                                        {categoryName}
                                    </span>
                                </div>
                            )}

                            {/* Title */}
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{post.title}</h1>

                            {/* Hero Image */}
                            {heroImageUrl && (
                                <div className="mb-6">
                                    <div className="relative w-full aspect-video md:aspect-[2/1] rounded-lg overflow-hidden">
                                        <Image
                                            src={heroImageUrl}
                                            alt={heroImage?.alt || post.title}
                                            fill
                                            sizes="(max-width: 768px) 100vw, 960px"
                                            className="object-cover"
                                            priority
                                            unoptimized
                                        />
                                    </div>
                                    <MediaCaption media={heroImage} />
                                    <MediaCredit media={heroImage} className="mt-1 text-right" />
                                </div>
                            )}

                            {/* Author and Metadata */}
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-6 border-b border-border">
                                {/* Authors */}
                                {post.populatedAuthors && post.populatedAuthors.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-foreground">
                                            {post.populatedAuthors
                                                .map((author) => author.name)
                                                .filter(Boolean)
                                                .join(", ")}
                                        </span>
                                    </div>
                                )}
                                {/* Date */}
                                {post.publishedAt && (
                                    <>
                                        <span className="text-muted-foreground/50">•</span>
                                        <ArticlePublishedInfo
                                            publishedAt={post.publishedAt}
                                            updatedAt={post.updatedAt}
                                            locale={locale}
                                        />
                                    </>
                                )}
                            </div>
                        </SectionStrip>

                        {/* Related Matches - Before Body Content */}
                        <RelatedMatches showHeader={false} />

                        {/* Article Body Content */}
                        <ArticleBody content={post.content} />

                        {/* Match detail block — shown when post is tagged with a match */}
                        <ArticleMatchDetail />

                        {/* Authors Section (detailed) */}
                        <ArticleAuthors authors={post.populatedAuthors} />

                        {/* Comments Section */}
                        {isAuthEnabled && (
                            <SectionStrip brandedStyle={sectionStyles?.[ARTICLE_RELATED]}>
                                <DiscussionWidget
                                    entityId={`post-${post.id}`}
                                    discussionLabel="Comments"
                                    className="mt-8"
                                />
                            </SectionStrip>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="w-full md:w-60 md:min-w-60 lg:w-75 lg:min-w-75 shrink-0">
                        <div className="sticky top-below-nav-gap space-y-6">
                            <AdSlot id="sidebar-top" />

                            {/* Tabbed Related Content (Related Articles + Latest from Category) */}
                            <TabbedRelatedContent variant="default" columns={1} className="pt-0" />

                            {/* Challenge Zone */}
                            <ChallengeZoneSidebar relatedEntities={relatedEntities} locale={localeStr} />

                            <AdSlot id="sidebar-bottom" />
                        </div>
                    </aside>
                </div>

                <AdSlot id="content-bottom" />
            </div>
        </article>
    );
}
